import { Conversation, Message } from '@twilio/conversations'
import filter from 'lodash-es/filter'
import values from 'lodash-es/values'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { Person } from '@/api/entities/people/types'
import {
  TextingStoreRefs,
  useOpenTextingSubtaskApi,
  useTextingStore,
  useTextingSubtaskByIdApi,
  // nosemgrep: no-v2-imports
} from '@/legacy/store/modules/textingV2'
import { IdMap } from '@/legacy/types/api/store'
import { TextCommunication } from '@/legacy/types/communications/communications'
import { ContactEntityWithRelationship } from '@/legacy/types/entities/entities'
import {
  MapEntityPhoneNumberPersonSms,
  MapEntityPhoneNumber,
} from '@/legacy/types/entities/phoneNumbers'
import { Contact } from '@/legacy/types/patients/contacts'
import { formatNameFromPerson } from '@/libs/format/format'
import { formatPhone } from '@/libs/phoneNumber'

/**
 * Function that takes in phone numbers and person off the patient and returns both in an array
 * @param phoneNumbers
 * @param person
 */
export function getPatientPhoneNumbersList(
  phoneNumbers: IdMap<MapEntityPhoneNumber>,
  person: Person
) {
  return values(phoneNumbers).map((phoneNumber) => ({
    ...phoneNumber,
    person,
  }))
}

/**
 *
 * Function that scrolls to:
 * - the last/most recent messages in the sms view
 * - the message box in the overall texting inbox view
 */
export function scrollIntoViewToLatestMessage() {
  const messageElements = document.getElementsByClassName('text-message')
  if (messageElements?.length) {
    // eslint-disable-next-line unicorn/prefer-at
    const lastMsg = messageElements[messageElements.length - 1]
    lastMsg.scrollIntoView(false)
  }
}

/**
 * Function that takes in an idmap of contacts and returns
 * a list of contact phone numbers and person object
 * @param contacts
 */
export function getContactsPhoneNumbersList(contacts: IdMap<Contact>) {
  const filteredContacts: ContactEntityWithRelationship[] = filter(
    values(contacts ?? {}),
    (contact: Contact) => !!contact.contactEntity && !!contact.approvedToSpeak
  ).map((contact: Contact) => ({
    ...contact.contactEntity,
    relationshipToPatient: contact.relationshipToPatient,
  }))

  return filteredContacts.flatMap(
    (contactEntity: ContactEntityWithRelationship) =>
      contactEntity.phoneNumbers?.map((phoneNumber: MapEntityPhoneNumber) => ({
        ...phoneNumber,
        person: contactEntity.person,
        ...(contactEntity.relationshipToPatient
          ? {
              relationshipToPatient: contactEntity.relationshipToPatient,
            }
          : {}),
      }))
  ) as MapEntityPhoneNumberPersonSms[]
}

/**
 * This function takes sms communications and retrieves all conversations
 * given an sms communication's sms thread's twilio conversation ids.
 *
 * IMPROVEME(MT-2751): there is no test for this functon as it uses twilio sdk methods.
 * @param smsComms
 */
export async function getConvosForSmsComms(smsComms: TextCommunication[]) {
  const smsCommsWithConversationIds = smsComms?.filter(
    (comm: TextCommunication) => !!comm.smsThread?.twilioConversationIds?.length
  )

  if (!smsCommsWithConversationIds?.length) {
    return
  }

  let errorMsg = null

  const convos = await Promise.all(
    smsCommsWithConversationIds?.flatMap(async (comm: TextCommunication) => {
      const sidArray = comm.smsThread.twilioConversationIds ?? []
      const hasMoreThanOneSid = sidArray.length && sidArray.length > 1
      try {
        if (hasMoreThanOneSid) {
          const conversations = await Promise.all(
            sidArray.map(
              async (conversationSid: string) =>
                await useTextingStore().getConversation(conversationSid)
            )
          )
          return conversations
        }
        const convo = await useTextingStore().getConversation(sidArray[0])
        return convo
      } catch (err) {
        errorMsg = `Couldn't fetch conversations ... ${err}, Convo: ${sidArray}`
        return
      }
    })
  )

  const flattenedConvos = convos
    .flat()
    .filter((convo) => !!convo) as Conversation[]
  const sortedConvos = flattenedConvos.sort(
    (a: Conversation, b: Conversation) => {
      if (!b.dateUpdated?.toISOString()) {
        return 1
      }
      if (!a.dateUpdated?.toISOString()) {
        return -1
      }
      return (
        DateTime.fromISO(a.dateUpdated?.toISOString()).toMillis() -
        DateTime.fromISO(b.dateUpdated?.toISOString()).toMillis()
      )
    }
  )
  return { convos: sortedConvos, error: errorMsg }
}

/**
 * This function takes a list of twilio conversations and gets messsages for each
 * conversation.
 *
 * IMPROVEME(MT-2751): there is no test for this functon as it uses twilio sdk methods.
 * @param convos
 */
export async function getMessagesForConvos(convos: Conversation[]): Promise<{
  messages: Message[]
  error: string | null
}> {
  const sortedConvos = convos.sort((a: Conversation, b: Conversation) => {
    const aDate = a?.lastMessage?.dateCreated
    const bDate = b?.lastMessage?.dateCreated

    // dateCreated is a Date object, compare via getTime()
    return (
      (aDate instanceof Date ? aDate.getTime() : 0) -
      (bDate instanceof Date ? bDate.getTime() : 0)
    )
  })

  // Use Promise.allSettled to fetch in parallel while handling individual failures.
  // This allows us to show messages from open conversations even if some are closed.
  const results = await Promise.allSettled(
    sortedConvos.map(async (convo: Conversation) => {
      const messagesData = await convo.getMessages()
      return messagesData.items
    })
  )

  const messages = results
    .filter(
      (r): r is PromiseFulfilledResult<Message[]> => r.status === 'fulfilled'
    )
    .flatMap((r) => r.value)

  const hasErrors = results.some((r) => r.status === 'rejected')

  return {
    messages,
    error:
      hasErrors && !messages.length
        ? "Couldn't fetch messages. Please try again."
        : null,
  }
}

/**
 *
 * Function that returns a map of attached media to its sid from the messages passed
 * @param messages
 * @param mediaLinks
 */
export async function getMediaFromMessages(
  messages: Message[],
  mediaLinks: Record<string, string>
) {
  for (const message of messages) {
    if (message.attachedMedia) {
      for (const media of message.attachedMedia) {
        mediaLinks[media.sid] = (await media.getContentTemporaryUrl()) as string
      }
    }
  }
  return mediaLinks
}

/**
 *
 * Function to check if textee is a patient based on their phone number
 * @param author
 * @param currentPhoneNumberStr
 */
export function isPatient(
  author: string | null,
  currentPhoneNumberStr: string
): boolean {
  return !!author && author === currentPhoneNumberStr
}

/**
 *
 * Function to return name of texteee or their phone number
 * @param author
 * @param people
 */
export function getTexteeNameOrFormatNumber(
  author: string | null,
  people: Person[] | undefined
): string {
  if (!author) {
    return 'N/A'
  }
  if (people?.length) {
    let nameStr = ''
    for (const [idx, person] of people.entries()) {
      if (idx !== 0) {
        nameStr += ', '
      }
      nameStr += formatNameFromPerson(person)
    }
    return nameStr
  }
  return formatPhone(author)
}

/**
 *
 * Function to check if content passed is a photo
 * @param contentType
 */
export function isPhoto(contentType: string) {
  return contentType.match(/^image\//g)
}

/**
 *
 * Function to check current communication has
 * shared numbers between different members.
 * @param smsCommunication
 * @param contacts
 */
export function hasSharedNumberWithAnotherMember(
  smsCommunication: TextCommunication,
  contacts: Contact[]
) {
  const { patientIds, smsThread } = smsCommunication
  if (patientIds.length > 1) {
    return true
  }

  // If patientIds.length === 1, check sms threads people list
  const { people } = smsThread
  if (people && people.length > 1) {
    const peopleEntityIds = people.map((person: Person) => person.entityId)
    const contactEntityIds = new Set(
      contacts.map((contact: Contact) => contact.contactEntityId)
    )
    // If a person entity id is not in patient ids list nor contact entity list,
    // it is another member entirely
    return !peopleEntityIds.every(
      (personEntityId) =>
        contactEntityIds.has(personEntityId) ||
        patientIds.includes(personEntityId)
    )
  }
  return false
}

/**
 *
 * Function to check current communication has
 * shared numbers between different members.
 * @param smsCommunication
 * @param contacts
 */
export function hasSharedNumberWithAnotherContact(
  smsCommunication: TextCommunication,
  contacts: Contact[]
) {
  const { smsThread } = smsCommunication
  if (smsThread.people && smsThread.people.length > 1) {
    const peopleEntityIds = new Set(
      smsThread.people.map((person: Person) => person.entityId)
    )
    const contactEntityIds = contacts.map(
      (contact: Contact) => contact.contactEntityId
    )
    return contactEntityIds.some((contactEntityId: string) =>
      peopleEntityIds.has(contactEntityId)
    )
  }
  return false
}

export type SharedNumberOther =
  // Someone in the current patient's `contacts` list — we know the
  // relationship and render it inline as plain text.
  | { type: 'contact'; contact: Contact }
  // A Thyme Care patient (i.e. another member). We link to their profile.
  | { type: 'member'; person: Person }
  // Not the current patient, not in their contacts, and not themselves
  // a patient — i.e. another member's contact whose relationship we don't
  // know. Render as plain text without a link.
  | { type: 'otherContact'; person: Person }

/**
 * Returns other people on this phone number (excluding patient & textee).
 * Tags each as 'contact' (known relationship) or 'member' (Thyme Care patient).
 * @param people
 * @param contacts
 * @param excludedEntityIds
 */
export function getOthersOnSharedNumber(
  people: Person[],
  contacts: Contact[],
  excludedEntityIds: string[]
): SharedNumberOther[] {
  const contactsByEntityId: Record<string, Contact> = Object.fromEntries(
    contacts.map((c) => [c.contactEntityId, c])
  )
  return people
    .filter((p) => !excludedEntityIds.includes(p.entityId))
    .map((person) => {
      const contact = contactsByEntityId[person.entityId]
      return contact ? { type: 'contact', contact } : { type: 'member', person }
    })
}

/**
 * Gets unread message count for conversation. Returns null if convo closed or not found.
 * @param convoSid
 */
export async function getUnreadMessageCount(convoSid: string | null) {
  if (!convoSid) return null
  try {
    const convo = await useTextingStore().getConversation(convoSid)
    if (convo && convo.state?.current !== 'closed') {
      return await convo.getUnreadMessagesCount()
    }
    return null
  } catch (err) {
    // Swallow "Not Found" errors (stale sid from deleted/archived Twilio convo).
    // Other errors (network, auth) bubble up.
    if (err instanceof Error && err.message === 'Not Found') return null
    throw err
  }
}

/**
 *
 * Function to fetch open pointer subtasks for a patient
 * @param patientId
 */
export async function getOpenPointerSubtasks(patientId: string) {
  await useOpenTextingSubtaskApi().list({
    params: {
      filter_patient_ids: [patientId],
    },
  })
}

/**
 * fetch subtask by its pointerToCommId
 * @param commId
 * @param patientId
 */
export async function getPointerSubtaskByCommunicationId(
  commId: string,
  patientId: string
) {
  await useTextingSubtaskByIdApi().list({
    params: {
      filter_pointer_comm_ids: [commId],
      filter_patient_ids: [patientId],
    },
  })
}

/**
 * Composable that builds a map of all textable phone numbers (patient + contacts)
 * for use with the text communication adapter.
 * @returns Computed map of phone number ID to phone number entity
 */
export function useAllTextablePhoneNumbers() {
  const { textableIndividuals } = storeToRefs(
    useTextingStore() as any
  ) as TextingStoreRefs

  return computed(() => {
    const map: Record<
      string,
      {
        phoneNumberId: string
        phoneNumber: MapEntityPhoneNumber['phoneNumber']
      }
    > = {}
    for (const textable of textableIndividuals.value) {
      map[textable.phoneNumberId] = {
        phoneNumberId: textable.phoneNumberId,
        phoneNumber: textable.phoneNumber,
      }
    }
    return map
  })
}
